import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { Card } from 'src/app/models/card';
import { Observable, Subscription } from 'rxjs';
import { CardTypeEnum } from '../../../enum/card-type.enum';
import { InteractionEnum } from '../../../enum/interaction.enum';
import { CardSearchService } from '../services/card-search.service';
import { CardHeader } from '../../../models/card-header';
import { debounceTime, distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { cardSummary } from '../../../utils/card-summary';
import { MatInput } from '@angular/material/input';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatOption } from '@angular/material/core';
import { IconComponent } from '../icon/icon.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AsyncPipe } from '@angular/common';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store';
import { selectShipmentEditorDestination, selectShipmentEditorShipper } from '../../../store/shipment-editor/shipment-editor.state';
import { contactEditorSetCard } from '../../../store/contact-editor/contact-editor.actions';
import { shipmentEditorUpdateDestination, shipmentEditorUpdateShipper } from '../../../store/shipment-editor/shipment-editor.actions';

@Component({
    selector: 'app-quick-card-form',
    templateUrl: './quick-card-form.component.html',
    styleUrls: ['./quick-card-form.component.scss'],
    imports: [ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatAutocompleteTrigger, MatSuffix, MatProgressSpinner, IconComponent, MatAutocomplete, MatOption, AsyncPipe]
})
export class QuickCardFormComponent implements OnInit, OnDestroy {

    cardTypes = CardTypeEnum;

    @Input()
    cardType: CardTypeEnum;

    @ViewChild(MatInput)
    searchInput: MatInput;

    @Output()
    updated: EventEmitter<InteractionEnum> = new EventEmitter<InteractionEnum>();

    searchControl: UntypedFormControl = new UntypedFormControl('');

    cardSearchBusy$: Observable<boolean>;
    cardSearch$: Observable<CardHeader[]>;
    cardSubscriber: Subscription;
    latestCard: Card = null;

    source$: Observable<Card>;

    formGroup = new FormGroup({
        attn: new FormControl(''),
        organization: new FormControl(''),
        country: new FormControl('US'),
        province: new FormControl(''),
        postalCode: new FormControl(''),
        city: new FormControl(''),
        street1: new FormControl(''),
        street2: new FormControl(''),
        street3: new FormControl(''),
        telephone: new FormControl(''),
        email: new FormControl(''),
        residentialFlag: new FormControl(false),
    });

    formSubscription: Subscription;

    constructor(private store: Store<AppState>, private cardSearchService: CardSearchService) {
        this.cardSearchBusy$ = this.cardSearchService.busy$.pipe(
            filter(() => this.searchInput?.focused)
        );
        this.cardSearch$ = this.searchControl.valueChanges.pipe(
            filter(() => this.searchInput?.focused),
            debounceTime(300),
            distinctUntilChanged(),
            switchMap((value) => this.cardSearchService.query(value))
        );
    }

    ngOnInit(): void {
        switch (this.cardType) {
            case CardTypeEnum.SHIPPER:
                this.source$ = this.store.select(selectShipmentEditorShipper);
                break;
            case CardTypeEnum.DESTINATION:
            default:
                this.source$ = this.store.select(selectShipmentEditorDestination);
                break;
        }
        this.cardSubscriber = this.source$.subscribe((card) => {
            this.formGroup.patchValue(card, { emitEvent: false });
            this.latestCard = card;
            this.searchControl.setValue(cardSummary(card));
        });
        this.formSubscription = this.formGroup.valueChanges.subscribe(value => this.cardUpdated(value));
    }

    ngOnDestroy(): void {
        this.cardSubscriber.unsubscribe();
    }

    cardUpdated(card: Partial<Card>): void {
        switch (this.cardType) {
            case CardTypeEnum.CONTACT:
                this.store.dispatch(contactEditorSetCard({ card }));
                break;
            case CardTypeEnum.SHIPPER:
                this.store.dispatch(shipmentEditorUpdateShipper({ card }));
                break;
            case CardTypeEnum.DESTINATION:
                this.store.dispatch(shipmentEditorUpdateDestination({ card }));
                break;
        }
    }

    onCardSearchSelection($event: MatAutocompleteSelectedEvent): boolean {
        this.searchControl.setValue('');
        this.cardSearchService.retrieve($event.option.value).subscribe((card) => {
            this.formGroup.patchValue(card, { emitEvent: false });
            this.userChange();
            this.searchControl.setValue(cardSummary(card));
            this.cardUpdated(card);
        });
        return false;
    }

    onBlur(): void {
        this.searchControl.setValue(cardSummary(this.latestCard), { emitEvent: false });
    }

    userChange(): void {
        this.updated.emit(InteractionEnum.DEFAULT);
    }
}
